import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/AddProduct";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@waytek/MobiusOverrides/Button/Button";
import { colorStrings } from "@waytek/Utilities/variables";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
    };
};

const mapDispatchToProps = {
    addProduct,
    searchProducts,
    setProduct,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    loadCurrentCart,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickOrderSearchStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    stockGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    spinnerContainer?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const quickOrderSearchStyles: QuickOrderSearchStyles = {
    container: {
        css: css`
            &&& {
                position: relative;
                &.loading {
                    opacity: 0.5;
                }
            }
        `,
    },

    searchGridItem: {
        width: [6, 3, 2, 2, 2],
    },

    descriptionGridItem: {
        width: [6, 5, 6, 6, 6],
        css: css`
            &&& {
                input {
                    background: ${getColor(colorStrings.commonGlobalWhite)};
                    cursor: auto;
                    color: ${getColor(colorStrings.textMain)};
                }
            }
        `,
    },

    stockGridItem: {
        width: [6, 2, 2, 2, 2],
        css: css`
            &&& {
                input {
                    background: ${getColor(colorStrings.commonGlobalWhite)};
                    cursor: auto;
                    color: ${getColor(colorStrings.textMain)};
                }
            }
        `,
    },

    qtyGridItem: {
        width: [6, 2, 2, 2, 2],
    },

    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    spinnerContainer: {
        css: css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: ${getColor(colorStrings.commonGlobalWhite)};
            opacity: 0.7;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = quickOrderSearchStyles;

const QuickOrderBulkSearch = ({
    setProduct,
    searchProducts,
    searchResults,
    selectedProductInfo,
    product,
    addCartLines,
    loadCurrentCart,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string[]>(Array.from({ length: 5 }, () => ""));
    const [currentField, setCurrentField] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    const [selectedProductInfoArray, setSelectedProductInfoArray] = useState<ProductInfo[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        reset();
    }, []);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        if (searchQuery && searchResults.length === 0) {
            handleErrors("No results found", currentField as number);
            return;
        }

        const productMatch = searchResults.find(item => item.erpNumber === searchQuery);
        let timeOut;
        let shouldCancel = false;

        if (!productMatch) {
            timeOut = setTimeout(() => {
                if (shouldCancel) {
                    handleErrors("No results found", currentField as number);
                }
            }, 1000);
            return;
        }

        if (productMatch) {
            shouldCancel = true;
            clearTimeout(timeOut);
            handleErrors("", currentField as number);
            setSelectedProductId(productMatch.id);

            const productTitle = productMatch.title;
            const descriptionInput = document.getElementById(
                `quickOrderDescriptionInput-${currentField}`,
            ) as HTMLInputElement;

            if (descriptionInput) {
                descriptionInput.value = productTitle;
            }
        }
    }, [searchResults]);

    useEffect(() => {
        if (selectedProductId) {
            setProduct({
                productId: selectedProductId,
                validateProduct: true,
                skipInventoryValidation: true,
                denyFixedConfiguration: true,
            });
        }
    }, [selectedProductId]);

    useEffect(() => {
        const stockField = document.getElementById(`quickOrderStockInput-${currentField}`) as HTMLInputElement;
        if (stockField && product) {
            stockField.value = (selectedProductInfo?.inventory?.qtyOnHand.toString() as string) || "N/A";
        }

        if (product) {
            const newSelectedProductInfoArray = [...selectedProductInfoArray];
            newSelectedProductInfoArray[currentField as number] = selectedProductInfo as ProductInfo;
            setSelectedProductInfoArray(newSelectedProductInfoArray);
        }
    }, [selectedProductInfo]);

    useEffect(() => {
        setIsLoading(false);
    }, [selectedProductInfoArray]);

    const debouncedSearchProducts = debounce((query: string) => {
        if (!query) {
            return;
        }
        setSearchQuery(query);
        searchProducts({ query });
    }, 1000);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        if (event.target.value !== "") {
            setCurrentField(index);
            setIsLoading(true);
            debouncedSearchProducts(event.target.value);
        } else {
            setTimeout(() => {
                handleErrors("", index);
                const updatedArray = [...selectedProductInfoArray];
                updatedArray[index] = {} as ProductInfo;
                setSelectedProductInfoArray(updatedArray);
            }, 500);
        }
    };

    function updateQty(event: ChangeEvent<HTMLInputElement>, index: number) {
        const updatedArray = [...selectedProductInfoArray];
        const newQty = event.target.value;

        updatedArray[index] = {
            ...updatedArray[index],
            qtyOrdered: parseInt(newQty, 10),
        };

        setSelectedProductInfoArray(updatedArray);
    }

    const handleErrors = (error: string, index: number) => {
        const newErrorMessages = [...errorMessage];
        newErrorMessages[index] = error;
        setErrorMessage(newErrorMessages);

        const fieldDescription = document.getElementById(`quickOrderDescriptionInput-${index}`) as HTMLInputElement;
        const fieldStock = document.getElementById(`quickOrderStockInput-${index}`) as HTMLInputElement;
        const fieldQty = document.getElementById(`quickOrderQtyInput-${index}`) as HTMLInputElement;

        if (fieldDescription) {
            fieldDescription.value = "";
        }

        if (fieldStock) {
            fieldStock.value = "";
        }

        if (fieldQty) {
            fieldQty.value = "";
        }

        setSelectedProductId(null);
        setSearchQuery("");
        setIsLoading(false);
        setSelectedProductId(null);
    };

    const reset = () => {
        setErrorMessage(["", "", "", "", ""]);
        setIsLoading(false);
        setSearchQuery("");
        setCurrentField(null);
        setSelectedProductId(null);

        // clear all input fields
        const fields = document.querySelectorAll("input");
        fields.forEach(field => {
            field.value = "";
        });
    };

    const isListValid = () => {
        return errorMessage.every(item => item === "") && selectedProductInfoArray.length > 0;
    };

    const addAllToCartClickHandler = async () => {
        if (!isListValid()) {
            return;
        }

        setIsSaving(true);

        const cartLinesCollection = (await addCartLines({ productInfos: selectedProductInfoArray })) as any;

        if(cartLinesCollection.cartLines.length === 0) {
            toasterContext.addToast({ body: "Error adding items to cart", messageType: "danger" });
            setIsSaving(false);
        }

        if (cartLinesCollection.cartLines.length > 0) {
            toasterContext.addToast({ body: "Items added to cart", messageType: "success" });
            reset();
            loadCurrentCart({ shouldLoadFullCart: true });
            setIsSaving(false);
        }
    };

    const renderSearchFields = () => {
        return Array.from({ length: 5 }, (_, index) => (
            <GridContainer {...styles.container} key={index}>
                <GridItem {...styles.searchGridItem}>
                    <TextField
                        onChange={event => onInputChanged(event, index)}
                        label={index === 0 ? "Part #" : ""}
                        type="text"
                        data-test-selector={`quickOrderSearchInput-${index}`}
                        placeholder="Search by Part #"
                        error={errorMessage[index]}
                        id={`quickOrderSearchInput-${index}`}
                    />
                </GridItem>
                <GridItem {...styles.descriptionGridItem}>
                    <TextField
                        label={index === 0 ? translate("Description") : ""}
                        disabled
                        type="text"
                        id={`quickOrderDescriptionInput-${index}`}
                    />
                </GridItem>
                <GridItem {...styles.stockGridItem}>
                    <TextField
                        label={index === 0 ? translate("Stock") : ""}
                        disabled
                        type="text"
                        id={`quickOrderStockInput-${index}`}
                    />
                </GridItem>
                <GridItem {...styles.qtyGridItem}>
                    <TextField
                        label={index === 0 ? translate("Qty") : ""}
                        id={`quickOrderQtyInput-${index}`}
                        type="text"
                        onChange={event => updateQty(event, index)}
                    />
                </GridItem>
            </GridContainer>
        ));
    };

    return (
        <StyledWrapper {...styles.container}>
            {renderSearchFields()}
            <GridContainer>
                <GridItem {...styles.buttonGridItem}>
                    <Button onClick={addAllToCartClickHandler} disabled={isLoading || !isListValid()}>
                        {translate("Add to Cart")}
                    </Button>
                </GridItem>
            </GridContainer>
            {isSaving && (<StyledWrapper {...styles.spinnerContainer}><LoadingSpinner {...styles.spinner} /></StyledWrapper>)}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(QuickOrderBulkSearch),
    definition: {
        group: "Quick Order",
        allowedContexts: ["QuickOrderPage"],
    },
};

export default widgetModule;
